import {
  Box,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  styled,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CheckIcon,
  DeleteIcon,
  EditIcon,
  FaceRetouchingNaturalIcon,
} from 'assets/icons';
import { Card, NumberField, Select } from 'components/commons';
import Alert from 'components/commons/Alert';
import { ConfirmDeleteDialog } from 'components/features/UserDetail';
import SecuredImage from 'components/hooks/SecuredImage';
import { API_PATHS } from 'constant/api';
import { GENDER } from 'constant/enum';
import { PAGE_LIST } from 'constant/pages';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  convertDateUTCToLocal,
  DatePattern,
  getKeyFromLocalStorage,
} from 'utils/constant';
import {
  deleteReference,
  getReferenceDetail,
  getVisits,
  updateReference,
} from 'utils/fetch';
import ja from 'utils/ja';

enum Gender {
  MALE = 'male',
  FEMALE = 'female',
}

const SelectGenderOpts: Option[] = [
  { label: ja.common.male, value: Gender.MALE },
  { label: ja.common.female, value: Gender.FEMALE },
];

const TypographyCustom = styled(Typography)(() => ({
  fontWeight: '400',
  lineHeight: '48px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: '1',
  WebkitBoxOrient: 'vertical',
}));

interface ITagType {
  tagName: string;
  status: boolean;
}

const UserDetail = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const appId = getKeyFromLocalStorage('appId');
  const appKey = getKeyFromLocalStorage('appKey');
  const dbId = getKeyFromLocalStorage('dbId');
  const dbKey = getKeyFromLocalStorage('dbKey');
  const tagLocal = localStorage.getItem('tags');

  const [visitQuery] = useState<IVisitQuery>({
    startTime: searchParams.get('startTime') ?? '',
    endTime: searchParams.get('endTime') ?? '',
    referenceId: id ?? '',
  });

  const [userInfo, setUserInfo] = useState<IReferenceDetail>();
  const [tagsStatus, setTagsStatus] = useState<ITagType[]>([]);
  const [enableEdit, setEnableEdit] = useState<boolean>(false);
  const [visits, setVisits] = useState<IDataVisit[]>([]);
  const [errorServer, setErrorServer] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [userUpdate, setUserUpdate] = useState<{
    age: number;
    gender: string;
    originalData: string;
  }>({ age: 0, gender: '', originalData: '' });

  const handleGetDetail = async () => {
    try {
      const data = await getReferenceDetail(id ?? '', {
        appId: appId,
        appKey: appKey,
        dbId: dbId,
        dbKey: dbKey,
      });
      if (data) {
        setUserInfo({
          ...data,
          gender:
            data.gender === GENDER.MALE ? ja.common.male : ja.common.female,
        });
        setUserUpdate({
          age: data.age,
          gender: data.gender,
          originalData: data.originalData,
        });
        const tagData = data.tag;
        if (tagData) {
          const tagRes: string[] = tagData.split(',');
          const tagLoop: ITagType[] = [];

          for (const t of tagRes) {
            if (
              (!tagLocal || !tagLocal?.includes(t)) &&
              !tagLoop.find((i) => i.tagName === t)
            ) {
              tagLoop.push({ tagName: t, status: true });
            }
          }
          setTagsStatus((oldState) => [
            ...tagLoop,
            ...oldState.map((item) => ({
              tagName: item.tagName,
              status: tagData.includes(item.tagName),
            })),
          ]);
        }
      }
    } catch (error) {
      //empty
    }
  };

  const handleGetVisits = async (visitQuery: IVisitQuery) => {
    const start = dayjs(visitQuery.startTime).startOf('day');
    const end = dayjs(visitQuery.endTime).endOf('day');

    const query = {
      startTime: start.utc().format(DatePattern.UTC_DATE),
      endTime: end.utc().format(DatePattern.UTC_DATE),
      referenceId: visitQuery.referenceId,
    };

    try {
      const { data } = await getVisits(query, {
        appId: appId ?? '',
        appKey: appKey ?? '',
        dbId: dbId ?? '',
        dbKey: dbKey ?? '',
      });
      const visitRes: IDataVisit[] = data[0].visits
        .map((visit: IVisits): IDataVisit => {
          const lastPointView = visit.pointViews[visit.pointViews.length - 1];
          const lastShot = lastPointView.shots[lastPointView.shots.length - 1];
          return {
            id: data[0].id.slice(-4),
            startTime: visit.startTime,
            endTime: visit.endTime,
            localtion: lastPointView.location,
            camera: lastShot.camera,
            shotId: lastShot.id,
          };
        })
        .sort((prev: IDataVisit, next: IDataVisit) => {
          const timePrev = dayjs(prev.endTime).valueOf();
          const timeNext = dayjs(next.endTime).valueOf();
          return timeNext - timePrev;
        });
      setVisits(visitRes);
    } catch (error) {
      // empty
    }
  };

  const handleSetTagValue = () => {
    if (tagLocal) {
      setTagsStatus(
        tagLocal
          .split(',')
          .map((tag: string) => ({ tagName: tag, status: false }))
      );
    }
  };

  const handleChangeTag = (tagName: string) => {
    const tagChanged = tagsStatus.map((tag) => {
      if (tag.tagName === tagName) {
        return {
          tagName: tag.tagName,
          status: !tag.status,
        };
      }
      return tag;
    });
    setTagsStatus(tagChanged);
  };

  const handleConfirmEdit = async () => {
    try {
      await updateReference(id ?? '', {
        appId: appId,
        appKey: appKey,
        dbId: dbId,
        dbKey: dbKey,
        tag: tagsStatus
          .filter((tag) => tag.status)
          .map((tag) => tag.tagName)
          .join(','),
        ...userUpdate,
      });
    } catch (error) {
      // empty
    }
    setEnableEdit(false);
  };

  const handleToVisitDetail = (visit: IDataVisit) => {
    return navigate(
      PAGE_LIST.VISIT_DETAIL.pathname(id) +
        `?startTime=${visit.startTime}&endTime=${visit.endTime}`
    );
  };

  const handleDeleteReference = async () => {
    try {
      await deleteReference(id ?? '', {
        appId: appId,
        appKey: appKey,
        dbId: dbId,
        dbKey: dbKey,
      });
      setOpenConfirm(false);
      navigate(-1);
    } catch (error) {
      setErrorServer(true);
      // empty
    }
  };

  useEffect(() => {
    handleSetTagValue();
    handleGetDetail();
  }, []);

  useEffect(() => {
    if (visitQuery) {
      handleGetVisits(visitQuery);
    }
  }, [visitQuery]);

  return (
    <Box
      sx={{
        flex: 1,
        height: { xs: 'calc(100dvh - 60px)', sm: 'calc(100dvh - 80px)' },
        mt: { xs: '60px', sm: '80px' },
        backgroundColor: '#F8F9FA',
        padding: '10px',
        overflowY: 'auto',
      }}
    >
      <Stack flexDirection='row' gap='10px'>
        <Box display='flex'>
          {!userInfo?.id && (
            <Skeleton
              variant='rectangular'
              width={148}
              height={148}
              animation='wave'
              sx={{ border: '1px solid #000000' }}
            />
          )}
          {userInfo?.id && (
            <SecuredImage
              src={`${API_PATHS.reference}/${userInfo.id}/image`}
              alt='face'
              style={{
                width: 148,
                height: 148,
                objectFit: 'cover',
                border: '1px solid #000000',
              }}
            />
          )}
        </Box>
        <Stack sx={{ flex: 1, gap: '10px' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography component='p'>
              {ja.user_detail.id}: {userInfo?.id.slice(-4)}{' '}
            </Typography>
            <IconButton
              sx={{
                width: 'fit-content',
                padding: '0',
                color: '#fff',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                fontSize: '16px !important',
                borderRadius: '4px',
              }}
              onClick={() => setOpenConfirm(true)}
            >
              <DeleteIcon
                sx={{
                  fontSize: '28px !important',
                  color: theme.palette.error.main,
                }}
              />
            </IconButton>
          </Box>
          <Typography>
            {ja.user_detail.first_date}:{' '}
            <Typography component='span' sx={{ whiteSpace: 'nowrap' }}>
              {convertDateUTCToLocal(
                userInfo?.createdAt ?? '',
                DatePattern.DATE_TIME
              )}
            </Typography>
          </Typography>
        </Stack>
      </Stack>
      <Box
        sx={{
          px: '10px',
          borderRadius: '4px',
          border: '1px solid #bababa',
          my: '10px',
        }}
      >
        {enableEdit ? (
          <Box sx={{ display: 'flex', mt: '10px', justifyContent: 'flex-end' }}>
            <Box
              component='img'
              src={CheckIcon}
              alt=''
              onClick={handleConfirmEdit}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              mt: '10px',
              justifyContent: 'flex-end',
              cursor: 'pointer',
            }}
          >
            <Box
              component='img'
              src={EditIcon}
              alt=''
              onClick={() => setEnableEdit(true)}
            />
          </Box>
        )}
        <Grid
          container
          direction='row'
          spacing={2}
          sx={{ mb: '4px', mt: '3px' }}
        >
          <Grid item xs={6}>
            <Select
              id='gender'
              label={ja.user_detail.gender}
              placeholder={ja.user_detail.gender_placeholder}
              options={SelectGenderOpts}
              notSetOpt
              value={userUpdate.gender}
              onChange={(event) =>
                setUserUpdate((oldState) => ({
                  ...oldState,
                  gender: event.target.value.toString(),
                }))
              }
              disabled={!enableEdit}
            />
          </Grid>

          <Grid item xs={6}>
            <NumberField
              id='age'
              label={ja.user_detail.age}
              placeholder={ja.user_detail.age_placeholder}
              disabled={!enableEdit}
              value={userUpdate.age}
              onChange={(value) =>
                setUserUpdate((oldState) => ({
                  ...oldState,
                  age: value ?? 0,
                }))
              }
            />
          </Grid>
        </Grid>
        <Typography>{ja.user_detail.tag}</Typography>
        <Box
          sx={{
            mb: '4px',
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: '10px',
            pb: '6px',
          }}
        >
          {tagsStatus.map(({ tagName, status }, index) => (
            <Box
              key={tagName + index}
              sx={{
                padding: '6px 12px',
                backgroundColor: theme.palette.primary.main,
                opacity: status ? 'unset' : '0.4',
                borderRadius: '16px',
                cursor: 'pointer',
                pointerEvents: enableEdit ? 'auto' : 'none',
              }}
              onClick={() => handleChangeTag(tagName)}
            >
              <Typography
                key={tagName}
                component='p'
                sx={{
                  minWidth: '98px',
                  color: '#fff',
                  textAlign: 'center',
                }}
              >
                {tagName}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography>{ja.user_detail.comment}</Typography>
        <TextField
          id='free-text'
          placeholder={ja.user_detail.free_text}
          multiline
          rows={3}
          disabled={!enableEdit}
          value={userUpdate.originalData}
          onChange={(event) =>
            setUserUpdate((oldState) => ({
              ...oldState,
              originalData: event.target.value,
            }))
          }
          InputProps={{
            inputComponent: 'textarea',
          }}
          sx={{
            width: '100%',
            mb: '10px',
            '& .MuiInputBase-root.Mui-disabled': {
              backgroundColor: '#dcdcdc',
            },
            '& .MuiInputBase-root > textarea': {
              padding: '0 6px',
            },
            '& .MuiInputBase-input.Mui-disabled': {
              color: '#000000',
              WebkitTextFillColor: '#000000',
            },
          }}
        />
      </Box>
      <Box sx={{ px: '6px', overflowX: 'auto' }}>
        <Box
          sx={{
            mb: '5px',
            display: 'grid',
            gridTemplateColumns: {
              xs: '60px 100px 100px 90px 90px 50px',
              sm: '0.8fr 1.2fr 1.2fr 1.2fr 1.2fr 0.4fr',
            },
            gap: '10px',
            px: '5px',
            backgroundColor: '#fff',
            borderRadius: '4px',
            width: { xs: 'fit-content', sm: '100%' },
          }}
        >
          {[
            ja.home.person_photo,
            ja.user_detail.camera,
            ja.user_detail.location,
            ja.home.start_time,
            ja.home.end_time,
          ].map((title, index) => (
            <Typography
              key={`title-${index}`}
              component='h4'
              sx={{ lineHeight: '32px', fontWeight: '700' }}
            >
              {title}
            </Typography>
          ))}
        </Box>
        {visits.map((visit) => (
          <Card
            key={visit.shotId}
            sx={{
              mb: '10px',
              gridTemplateColumns: {
                xs: '60px 100px 100px 90px 90px 50px',
                sm: '0.8fr 1.2fr 1.2fr 1.2fr 1.2fr 0.4fr',
              },
            }}
          >
            <Box display='flex'>
              <SecuredImage
                src={`${API_PATHS.get_face_image(visit.shotId)}`}
                alt='face'
                style={{
                  width: 48,
                  height: 48,
                  objectFit: 'cover',
                  border: '1px solid #000000',
                }}
              />
            </Box>
            <TypographyCustom>{visit.camera}</TypographyCustom>
            <TypographyCustom>{visit.localtion}</TypographyCustom>
            <TypographyCustom>
              {convertDateUTCToLocal(visit.startTime, DatePattern.TIME)}
            </TypographyCustom>
            <TypographyCustom>
              {convertDateUTCToLocal(visit.endTime, DatePattern.TIME)}
            </TypographyCustom>
            <IconButton onClick={() => handleToVisitDetail(visit)}>
              <FaceRetouchingNaturalIcon
                sx={{
                  fill: theme.palette.primary.main,
                  fontSize: '32px !important',
                }}
              />
            </IconButton>
          </Card>
        ))}
      </Box>
      <Alert
        open={errorServer}
        onClose={() => setErrorServer(false)}
        message={ja.message_error.internal_server}
        type='error'
      />
      <ConfirmDeleteDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        handleConfirm={handleDeleteReference}
      />
    </Box>
  );
};

export default UserDetail;
